import { AsyncBoundary, Card } from '@kollez/design-system';
import { useFacultyMe } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /faculty/profile — the caller's own FacultyProfile (5c schema).
 *
 * Surfaces the rich Ship 5c data that had no viewer before: qualifications,
 * subjects taught, role assignments across colleges, publications, honours,
 * PhD status. Read-only for MVP; edit will land with a Nexie-drafted
 * rectification request per DPDP §14 flow.
 */

const TYPE_LABEL: Record<string, string> = {
  permanent: 'Permanent',
  adjunct: 'Adjunct',
  visiting: 'Visiting',
  guest: 'Guest',
  contract: 'Contract',
};
const PHD_LABEL: Record<string, string> = {
  holds_phd: 'Holds PhD',
  pursuing_phd: 'Pursuing PhD',
  not_pursuing: 'No PhD',
};
const STATUS_TONE: Record<string, string> = {
  active: 'badge-success',
  on_leave: 'badge-warning',
  sabbatical: '',
  retired: '',
  terminated: 'badge-danger',
};

export function FacultyProfilePage() {
  const q = useFacultyMe();
  const p = q.data ?? null;

  return (
    <AsyncBoundary
      kind="detail"
      loading={q.isLoading}
      error={q.error}
      empty={!q.isLoading && !p}
      emptyTitle="No faculty profile"
      emptyHint="Your college's registrar needs to create your FacultyProfile record before it appears here."
      onRetry={() => void q.refetch()}
    >
      {p && (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">
                Faculty · {typeof p.department === 'object' && p.department ? p.department.name : 'Department'}
              </p>
              <h1 className="h-page">{p.designation}</h1>
              <p className="subhead">
                <span className={`badge ${STATUS_TONE[p.status] ?? ''}`}>{p.status.replace(/_/g, ' ')}</span>
                {' · '}
                <span className="badge">{TYPE_LABEL[p.type] ?? p.type}</span>
                {' · '}
                <span className="badge">{PHD_LABEL[p.phdStatus] ?? p.phdStatus}</span>
                {p.employeeId && <> · Employee ID {p.employeeId}</>}
                {' · joined '}
                {formatIndianDate(new Date(p.dateOfJoining))}
              </p>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
            <div className="stack">
              <Card padding="md">
                <h2 className="h-section">Qualifications</h2>
                {p.qualifications.length === 0 ? (
                  <p className="cell-sub m-0">No qualifications on file.</p>
                ) : (
                  <ol className="stack">
                    {p.qualifications.map((qual, i) => (
                      <li key={i} className="p-3 border border-line-tertiary rounded">
                        <div className="font-medium">
                          {qual.degreeName} ({qual.degree}) — {qual.year}
                        </div>
                        <div className="cell-sub">
                          {qual.specialization && <>{qual.specialization} · </>}
                          {qual.institution ?? qual.university ?? '—'}
                          {qual.class && <> · {qual.class}</>}
                          {qual.cgpaOrPercentage && <> · {qual.cgpaOrPercentage}</>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Card>

              <Card padding="md">
                <h2 className="h-section">Subjects taught</h2>
                {p.subjectsTaught.length === 0 ? (
                  <p className="cell-sub m-0">No subjects on file.</p>
                ) : (
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Subject</th>
                          <th className="hide-mobile">Level</th>
                          <th className="hide-mobile">Sem</th>
                          <th className="r">Credits</th>
                          <th>Now?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.subjectsTaught.map((s, i) => (
                          <tr key={i}>
                            <td className="mono">{s.subjectCode}</td>
                            <td>
                              <div className="cell-title">{s.subjectName}</div>
                              {(s.programmeCode || s.specializationCode) && (
                                <div className="cell-sub">
                                  {[s.programmeCode, s.specializationCode].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>
                            <td className="hide-mobile">{s.level}</td>
                            <td className="hide-mobile">{s.semester ?? '—'}</td>
                            <td className="r">{s.creditHours ?? '—'}</td>
                            <td>
                              {s.currentSemester ? <span className="badge badge-success">Yes</span> : <span className="badge">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card padding="md">
                <h2 className="h-section">Role assignments</h2>
                {p.roleAssignments.length === 0 ? (
                  <p className="cell-sub m-0">No role assignments recorded.</p>
                ) : (
                  <ol className="stack">
                    {p.roleAssignments.map((r, i) => (
                      <li key={i} className="p-3 border border-line-tertiary rounded">
                        <div className="font-medium">
                          {r.role.replace(/_/g, ' ')} {r.active && <span className="badge badge-success ml-1">Active</span>}
                        </div>
                        <div className="cell-sub">
                          {formatIndianDate(new Date(r.from))}
                          {r.to && <> → {formatIndianDate(new Date(r.to))}</>}
                          {r.notes && <> · {r.notes}</>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Card>

              {p.experience?.previousInstitutions && p.experience.previousInstitutions.length > 0 && (
                <Card padding="md">
                  <h2 className="h-section">
                    Previous institutions
                    {p.experience.years && <> · {p.experience.years} yrs experience</>}
                  </h2>
                  <ol className="stack">
                    {p.experience.previousInstitutions.map((pi, i) => (
                      <li key={i} className="p-3 border border-line-tertiary rounded">
                        <div className="font-medium">{pi.institutionName}</div>
                        <div className="cell-sub">
                          {pi.designation && <>{pi.designation} · </>}
                          {formatIndianDate(new Date(pi.from))}
                          {pi.to && <> → {formatIndianDate(new Date(pi.to))}</>}
                          {pi.rolesHeld && pi.rolesHeld.length > 0 && <> · {pi.rolesHeld.join(', ')}</>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
            </div>

            <aside className="stack">
              <Card padding="md">
                <h2 className="h-section">Contact</h2>
                <dl className="dl">
                  {p.contact?.officialEmail && (<>
                    <dt>Work email</dt><dd>{p.contact.officialEmail}</dd>
                  </>)}
                  {p.contact?.personalEmail && (<>
                    <dt>Personal email</dt><dd>{p.contact.personalEmail}</dd>
                  </>)}
                  {p.contact?.mobile && (<>
                    <dt>Mobile</dt><dd>{p.contact.mobile}</dd>
                  </>)}
                  {p.contact?.extension && (<>
                    <dt>Extension</dt><dd>{p.contact.extension}</dd>
                  </>)}
                  {p.contact?.cabin && (<>
                    <dt>Cabin</dt><dd>{p.contact.cabin}</dd>
                  </>)}
                </dl>
              </Card>

              {p.researchAreas.length > 0 && (
                <Card padding="md">
                  <h2 className="h-section">Research areas</h2>
                  <div className="flex flex-wrap gap-1">
                    {p.researchAreas.map((r, i) => (
                      <span key={i} className="badge">{r}</span>
                    ))}
                  </div>
                </Card>
              )}

              {p.publications && (
                <Card padding="md">
                  <h2 className="h-section">Publications</h2>
                  <dl className="dl">
                    {typeof p.publications.count === 'number' && (<>
                      <dt>Papers</dt><dd>{p.publications.count}</dd>
                    </>)}
                    {typeof p.publications.citationCount === 'number' && (<>
                      <dt>Citations</dt><dd>{p.publications.citationCount}</dd>
                    </>)}
                    {typeof p.publications.hIndex === 'number' && (<>
                      <dt>h-index</dt><dd>{p.publications.hIndex}</dd>
                    </>)}
                    {typeof p.publications.booksAuthored === 'number' && (<>
                      <dt>Books</dt><dd>{p.publications.booksAuthored}</dd>
                    </>)}
                    {typeof p.publications.patents === 'number' && (<>
                      <dt>Patents</dt><dd>{p.publications.patents}</dd>
                    </>)}
                    {p.publications.orcidId && (<>
                      <dt>ORCID</dt><dd>{p.publications.orcidId}</dd>
                    </>)}
                    {p.publications.googleScholarUrl && (<>
                      <dt>Scholar</dt>
                      <dd><a href={p.publications.googleScholarUrl} className="link" target="_blank" rel="noreferrer">Profile</a></dd>
                    </>)}
                  </dl>
                </Card>
              )}

              {p.honours && p.honours.length > 0 && (
                <Card padding="md">
                  <h2 className="h-section">Honours &amp; awards</h2>
                  <ol className="stack">
                    {p.honours.map((h, i) => (
                      <li key={i}>
                        <div className="font-medium">{h.title}</div>
                        <div className="cell-sub">
                          {h.awardedBy}{h.year && <> · {h.year}</>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
            </aside>
          </div>
        </div>
      )}
    </AsyncBoundary>
  );
}
